import { validateEmail } from '@/components/contacts/schema';
import { Icon, Icons } from '@/components/icon';
import { Popover, PopoverContent, PopoverScrollArea } from '@/components/ui/popover';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { PopoverAnchor } from '@radix-ui/react-popover';
import * as React from 'react';
import { TableCell } from '../table';

function Email({ email }: { email: string }) {
  return <P3 className="flex-none truncate text-primary-100">{email}</P3>;
}

function EmailInput({
  value,
  onChange,
  onSubmit,
  inputRef: externalInputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (email: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const { t } = useTranslation('desktop_ui');
  const internalInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value && onSubmit) {
      e.preventDefault();
      e.stopPropagation();
      const trimmedEmail = value.trim();
      if (validateEmail(trimmedEmail)) {
        onSubmit(trimmedEmail);
        onChange('');
      }
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Icon name={Icons.At} className="text-s text-primary-40" />
      <input
        ref={inputRef}
        type="email"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={t('components.contacts.form.placeholderEmail')}
        className="w-full caret-highlight-default bg-transparent text-primary-100 outline-none font-medium text-s placeholder:text-primary-60"
      />
    </div>
  );
}

// Wrapper to isolate popover state from parent re-renders
function EditableEmailCell({
  children,
  emails,
  isOpen,
  onOpenChange,
  onEmailAdd,
  onEmailDelete,
}: {
  children: React.ReactNode;
  emails: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onEmailAdd?: (email: string) => void;
  onEmailDelete?: (email: string) => void;
}) {
  return (
    <EmailPopover
      emails={emails}
      open={isOpen}
      onOpenChange={onOpenChange}
      onAddEmail={onEmailAdd}
      onDeleteEmail={onEmailDelete}
    >
      {children}
    </EmailPopover>
  );
}

export interface EmailCellHandle {
  open: () => void;
}

export const EmailCell = React.memo(
  React.forwardRef<
    EmailCellHandle,
    {
      contactId?: string;
      emails?: string[];
      isOpen?: boolean;
      onOpenChange?: (open: boolean) => void;
      /** Stable callback - receives contactId and currentEmails to avoid closures */
      onEmailAdd?: (contactId: string, currentEmails: string[], email: string) => void;
      /** Stable callback - receives contactId and currentEmails to avoid closures */
      onEmailDelete?: (contactId: string, currentEmails: string[], email: string) => void;
    }
  >(function EmailCell(
    { contactId, emails, isOpen: controlledIsOpen, onOpenChange, onEmailAdd, onEmailDelete },
    ref,
  ) {
    const [internalIsOpen, setInternalIsOpen] = React.useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = React.useCallback(
      (open: boolean) => {
        if (isControlled) {
          onOpenChange?.(open);
        } else {
          setInternalIsOpen(open);
        }
      },
      [isControlled, onOpenChange],
    );

    const multiple = emails?.length ? emails.length > 1 : false;

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsOpen(true);
        },
      }),
      [setIsOpen],
    );

    // Wrap callbacks to include contactId and emails for the stable parent callback
    const handleEmailAdd = React.useCallback(
      (email: string) => {
        if (contactId) {
          onEmailAdd?.(contactId, emails ?? [], email);
        }
      },
      [contactId, emails, onEmailAdd],
    );

    const handleEmailDelete = React.useCallback(
      (email: string) => {
        if (contactId) {
          onEmailDelete?.(contactId, emails ?? [], email);
        }
      },
      [contactId, emails, onEmailDelete],
    );

    const cellContent = (
      <>
        <TableCell
          className={cn(
            'overflow-hidden px-2',
            multiple && 'gap-1',
            multiple &&
              'group-focus:min-h-[65px] group-focus:max-h-[192px] group-focus:overflow-y-auto group-focus:flex-col group-focus:items-start group-focus:py-2.5',
          )}
        >
          {emails?.map((email) => (
            <Email key={email} email={email} />
          ))}
        </TableCell>
        {/* Expanded overlay on focus */}
        <TableCell
          className={cn(
            'pointer-events-none absolute left-0 top-0 hidden w-[150%] overflow-hidden rounded-xs border-highlight-default bg-white px-2 group-focus:pointer-events-auto group-focus:flex',
            multiple &&
              'group-focus:min-h-[65px] group-focus:max-h-[192px] group-focus:overflow-y-auto group-focus:flex-col group-focus:items-start group-focus:gap-1 group-focus:py-2.5',
          )}
        >
          {emails?.map((email) => (
            <Email key={email} email={email} />
          ))}
        </TableCell>
      </>
    );

    // If no contactId, just render the cell without popover (read-only mode)
    if (!contactId) {
      return cellContent;
    }

    return (
      <EditableEmailCell
        emails={emails ?? []}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onEmailAdd={handleEmailAdd}
        onEmailDelete={handleEmailDelete}
      >
        {cellContent}
      </EditableEmailCell>
    );
  }),
);

export function EmailPopover({
  children,
  emails,
  open: controlledOpen,
  onOpenChange,
  onAddEmail,
  onDeleteEmail,
  collisionBoundary,
  align = 'start',
  side,
  sideOffset = -41,
}: {
  children: React.ReactNode;
  emails: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAddEmail?: (email: string) => void;
  onDeleteEmail?: (email: string) => void;
  collisionBoundary?: Element | Element[] | null;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}) {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = React.useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
      }
    },
    [isControlled, onOpenChange],
  );
  const [orderedEmails, setOrderedEmails] = React.useState<string[]>(emails);
  const [emailInputValue, setEmailInputValue] = React.useState('');
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const emailInputRef = React.useRef<HTMLInputElement>(null);

  // Sync ordered emails when emails prop changes
  React.useEffect(() => {
    if (open) {
      setOrderedEmails(emails);
    }
  }, [open, emails]);

  // Reset input value when popover closes
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (!open && prevOpenRef.current) {
      // Popover just closed - reset input
      setEmailInputValue('');
    }
    prevOpenRef.current = open;
  }, [open]);

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (index < orderedEmails.length - 1) {
        // Move to next item
        itemRefs.current[index + 1]?.focus();
      } else {
        // At the last item, focus the email input
        emailInputRef.current?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (index > 0) {
        // Move to previous item
        itemRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Only propagate if the state is actually changing - ignore spurious Radix calls during re-renders
      if (newOpen === open) {
        return;
      }
      setOpen(newOpen);
      // When closing the popover, restore focus to the original cell
      if (!newOpen) {
        // Find the focusable cell element (parent of the anchor)
        const cell = triggerRef.current?.closest('[tabindex]') as HTMLElement | null;
        if (cell) {
          // Use requestAnimationFrame to ensure focus happens after popover fully closes
          requestAnimationFrame(() => {
            cell.focus();
          });
        }
      }
    },
    [setOpen, open],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverAnchor
        onDoubleClick={() => {
          setOpen(true);
        }}
        ref={triggerRef}
      >
        {children}
      </PopoverAnchor>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionBoundary={collisionBoundary}
        className="z-popover w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-2xl bg-white"
        onOpenAutoFocus={(e) => {
          // Prevent default focus behavior and focus the email input instead
          e.preventDefault();
          emailInputRef.current?.focus();
        }}
        onKeyDown={(e) => {
          // Prevent Enter from closing the popover
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {orderedEmails.length > 0 && (
          <PopoverScrollArea className="max-h-[192px]">
            <div className="relative pt-2.5 pr-[11px] pb-1.5 pl-2">
              {orderedEmails.map((email, index) => (
                <div
                  key={email}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  tabIndex={-1}
                  onKeyDown={(e) => handleItemKeyDown(e, index)}
                  className="group/row mb-1 flex select-none items-center gap-2 rounded-md px-1 -mx-1 outline-none"
                >
                  <span
                    className="mr-auto"
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        // Focus the bin button (next sibling)
                        const bin = e.currentTarget.nextElementSibling as HTMLElement;
                        bin?.focus();
                      } else if (e.key === 'Tab' && e.shiftKey) {
                        e.preventDefault();
                        // Focus previous row's bin, or wrap to email input
                        const row = e.currentTarget.parentElement;
                        const prevRow = row?.previousElementSibling;
                        if (prevRow) {
                          const prevBin = prevRow.querySelector(
                            'button:last-of-type',
                          ) as HTMLElement;
                          prevBin?.focus();
                        } else {
                          emailInputRef.current?.focus();
                        }
                      }
                    }}
                  >
                    <Email email={email} />
                  </span>
                  <button
                    type="button"
                    tabIndex={0}
                    className="group/bin cursor-pointer p-1 text-primary-40 opacity-0 outline-none transition-opacity hover:text-extra-decline hover:opacity-100 focus:opacity-100 group-hover/row:opacity-100"
                    onClick={() => onDeleteEmail?.(email)}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        // Focus next row's email, or email input
                        const row = e.currentTarget.parentElement;
                        const nextRow = row?.nextElementSibling;
                        if (nextRow) {
                          const nextEmail = nextRow.querySelector('span button') as HTMLElement;
                          nextEmail?.focus();
                        } else {
                          emailInputRef.current?.focus();
                        }
                      } else if (e.key === 'Tab' && e.shiftKey) {
                        e.preventDefault();
                        // Focus the email in same row (previous sibling's button)
                        const emailSpan = e.currentTarget.previousElementSibling as HTMLElement;
                        const emailEl = emailSpan?.querySelector('button') as HTMLElement;
                        emailEl?.focus();
                      }
                    }}
                  >
                    <span className="flex rounded-xs group-focus/bin:ring-2 group-focus/bin:ring-primary-40">
                      <Icon name={Icons.Bin} className="text-s" />
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </PopoverScrollArea>
        )}
        <div
          className={cn('px-2 py-2.5', orderedEmails.length > 0 && 'border-t border-primary-5')}
          onKeyDown={(e) => {
            // Handle Tab from email input to wrap to first email
            if (e.key === 'Tab' && !e.shiftKey && e.target === emailInputRef.current) {
              if (orderedEmails.length > 0) {
                e.preventDefault();
                // Focus the first email in the list
                const firstRow = itemRefs.current[0];
                const firstEmail = firstRow?.querySelector('span button') as HTMLElement;
                firstEmail?.focus();
              }
            }
          }}
        >
          <EmailInput
            value={emailInputValue}
            onChange={setEmailInputValue}
            inputRef={emailInputRef}
            onSubmit={(email) => {
              onAddEmail?.(email);
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
